import { useState, useMemo, useCallback } from "react";
import { useAppContext } from "../../../AppContext";
import { useApiMutation } from "../../infrastructure/hooks/useApiMutation";
import { useApiQuery } from "../../infrastructure/hooks/useApiQuery";
import { ICategorizedNomenclatureEndpoints } from "../models/categorizedNomenclatureEndpoints";
import {
  ICategorizableItem,
  INomenclatureCategory,
  ICategorizedResponse,
} from "../models/categorizedNomenclatureModels";

export function useCategorizedNomenclature<
  TItem extends ICategorizableItem,
  TCategory extends INomenclatureCategory
>(
  endpoints: ICategorizedNomenclatureEndpoints,
  keysToInvalidate: (string | number | undefined)[][]
) {
  const [itemDialogOpen, setItemDialogOpen] = useState(false);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [editingItemId, setEditingItemId] = useState<number | null>(null);
  const [editingCategoryId, setEditingCategoryId] = useState<number | null>(null);
  const [reorderItemsDialogOpen, setReorderItemsDialogOpen] = useState(false);
  const [reorderCategoriesDialogOpen, setReorderCategoriesDialogOpen] = useState(false);
  const [reorderingCategoryId, setReorderingCategoryId] = useState<number | null | undefined>(undefined);

  const { selectedStudioId } = useAppContext();
  const { mutateAsync } = useApiMutation();
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch categorized data
  const {
    data: categorizedData,
    isLoading,
    refetch,
  } = useApiQuery<ICategorizedResponse<TItem, TCategory>>({
    queryKey: [endpoints.getAllCategorized, selectedStudioId],
    endpoint: endpoints.getAllCategorized,
    enabled: !!selectedStudioId,
    globalIsLoading: false,
  });

  // Memoized categories and items
  const categories = useMemo(() => categorizedData?.categories ?? [], [categorizedData]);
  const items = useMemo(() => categorizedData?.items ?? [], [categorizedData]);

  // Group items by category
  const itemsByCategory = useMemo(() => {
    const grouped: Record<number | "uncategorized", TItem[]> = { uncategorized: [] };

    categories.forEach((cat) => {
      grouped[cat.id] = [];
    });

    items.forEach((item) => {
      if (item.categoryId && grouped[item.categoryId]) {
        grouped[item.categoryId].push(item);
      } else {
        grouped.uncategorized.push(item);
      }
    });

    return grouped;
  }, [categories, items]);

  // Item CRUD operations
  const createItem = useCallback(async (data: Partial<TItem>) => {
    try {
      setIsSubmitting(true);
      await mutateAsync(
        {
          endpoint: endpoints.create,
          method: "post",
          globalIsLoading: false,
          invalidateKeys: keysToInvalidate,
          ...data,
        },
        {
          onSettled: () => {
            setIsSubmitting(false);
            setItemDialogOpen(false);
            setEditingItemId(null);
          },
        }
      );
    } catch {
      setIsSubmitting(false);
    }
  }, [endpoints, keysToInvalidate, mutateAsync]);

  const updateItem = useCallback(async (id: number, data: Partial<TItem>) => {
    try {
      setIsSubmitting(true);
      await mutateAsync(
        {
          endpoint: `${endpoints.update}?id=${id}`,
          method: "put",
          globalIsLoading: false,
          invalidateKeys: keysToInvalidate,
          ...data,
        },
        {
          onSettled: () => {
            setIsSubmitting(false);
            setItemDialogOpen(false);
            setEditingItemId(null);
          },
        }
      );
    } catch {
      setIsSubmitting(false);
    }
  }, [endpoints, keysToInvalidate, mutateAsync]);

  const reorderItems = useCallback(async (categoryId: number | null, orderedIds: number[]) => {
    try {
      setIsSubmitting(true);
      await mutateAsync(
        {
          endpoint: endpoints.reorderItems,
          method: "put",
          globalIsLoading: false,
          invalidateKeys: keysToInvalidate,
          categoryId,
          orderedIds,
        } as any,
        {
          onSettled: () => {
            setIsSubmitting(false);
            setReorderItemsDialogOpen(false);
            setReorderingCategoryId(undefined);
          },
        }
      );
    } catch {
      setIsSubmitting(false);
    }
  }, [endpoints, keysToInvalidate, mutateAsync]);

  // Category CRUD operations
  const createCategory = useCallback(async (data: Partial<TCategory>) => {
    try {
      setIsSubmitting(true);
      await mutateAsync(
        {
          endpoint: endpoints.createCategory,
          method: "post",
          globalIsLoading: false,
          invalidateKeys: keysToInvalidate,
          ...data,
        },
        {
          onSettled: () => {
            setIsSubmitting(false);
            setCategoryDialogOpen(false);
            setEditingCategoryId(null);
          },
        }
      );
    } catch {
      setIsSubmitting(false);
    }
  }, [endpoints, keysToInvalidate, mutateAsync]);

  const updateCategory = useCallback(async (id: number, data: Partial<TCategory>) => {
    try {
      setIsSubmitting(true);
      await mutateAsync(
        {
          endpoint: `${endpoints.updateCategory}?id=${id}`,
          method: "put",
          globalIsLoading: false,
          invalidateKeys: keysToInvalidate,
          ...data,
        },
        {
          onSettled: () => {
            setIsSubmitting(false);
            setCategoryDialogOpen(false);
            setEditingCategoryId(null);
          },
        }
      );
    } catch {
      setIsSubmitting(false);
    }
  }, [endpoints, keysToInvalidate, mutateAsync]);

  const reorderCategories = useCallback(async (orderedIds: number[]) => {
    try {
      setIsSubmitting(true);
      await mutateAsync(
        {
          endpoint: endpoints.reorderCategories,
          method: "put",
          globalIsLoading: false,
          invalidateKeys: keysToInvalidate,
          payload: { orderedIds },
        } as any,
        {
          onSettled: () => {
            setIsSubmitting(false);
            setReorderCategoriesDialogOpen(false);
          },
        }
      );
    } catch {
      setIsSubmitting(false);
    }
  }, [endpoints, keysToInvalidate, mutateAsync]);

  const toggleCategoryDeletionStatus = useCallback(async (categoryId: number) => {
    try {
      setIsSubmitting(true);
      await mutateAsync(
        {
          endpoint: `${endpoints.toggleCategoryDeletionStatus}?categoryId=${categoryId}`,
          method: "put",
          globalIsLoading: false,
          invalidateKeys: keysToInvalidate,
        },
        {
          onSettled: () => {
            setIsSubmitting(false);
          },
        }
      );
    } catch {
      setIsSubmitting(false);
    }
  }, [endpoints, keysToInvalidate, mutateAsync]);

  // Open reorder dialog for a specific category
  const openReorderItemsDialog = useCallback((categoryId?: number | null) => {
    setReorderingCategoryId(categoryId);
    setReorderItemsDialogOpen(true);
  }, []);

  // Get items for reordering (by category)
  const getItemsForReorder = useCallback((categoryId?: number | null): TItem[] => {
    if (categoryId === null || categoryId === undefined) {
      return itemsByCategory.uncategorized.filter(item => !item.isDeleted);
    }
    return (itemsByCategory[categoryId] ?? []).filter(item => !item.isDeleted);
  }, [itemsByCategory]);

  return {
    // Data
    categories,
    items,
    itemsByCategory,
    isLoading,
    selectedStudioId,
    isSubmitting,

    // Item dialog state
    itemDialogOpen,
    editingItemId,
    setItemDialogOpen,
    setEditingItemId,

    // Category dialog state
    categoryDialogOpen,
    editingCategoryId,
    setCategoryDialogOpen,
    setEditingCategoryId,

    // Reorder dialogs state
    reorderItemsDialogOpen,
    reorderCategoriesDialogOpen,
    reorderingCategoryId,
    setReorderItemsDialogOpen,
    setReorderCategoriesDialogOpen,
    openReorderItemsDialog,
    getItemsForReorder,

    // Operations
    refetch,
    createItem,
    updateItem,
    reorderItems,
    createCategory,
    updateCategory,
    reorderCategories,
    toggleCategoryDeletionStatus,
  };
}
